/**
 * Basecamp "My Stuff" sidebar widget - compact summary linking to Todos table.
 * Legacy integration sidebar only (BasecampSidebar / test-page); not primary nav.
 *
 * Related: docs/product/bets/2026-03-17-basecamp-view-consolidation.md (Phase 2.6)
 */

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Clock,
  Pin,
  ListTodo,
} from 'lucide-react';
import { useBasecampAssignments } from '@/hooks/use-basecamp';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { StorageKeys, StorageSchemas } from '@/lib/storage';
import { cn } from '@/lib/utils';
import {
  MyStuffSummary,
  openBasecampTriageTab,
  getMyStuffCounts,
} from './my-stuff-summary';

export function BasecampMyStuffSidebar() {
  const [isExpanded, setIsExpanded] = usePersistedState({
    key: StorageKeys.BASECAMP_MY_STUFF_EXPANDED,
    defaultValue: true,
    schema: StorageSchemas.boolean,
  });
  const [bookmarkedOnly] = usePersistedState({
    key: StorageKeys.BASECAMP_ASSIGNMENTS_BOOKMARKED_ONLY,
    defaultValue: false,
    schema: StorageSchemas.boolean,
  });
  const { assignments, isLoading, error, refetch, cached, cachedAt, stale } =
    useBasecampAssignments(bookmarkedOnly);

  const { total: totalCount, overdue: overdueCount } = getMyStuffCounts(assignments);

  const getRelativeTime = (date: Date | string) => {
    const now = new Date();
    const target = new Date(date);
    const diffMs = now.getTime() - target.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className="flex-shrink-0 flex flex-col h-full min-h-0 overflow-hidden" data-testid="basecamp-my-stuff-sidebar">
      <div className="px-3 py-2 flex items-center justify-between flex-shrink-0">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 flex items-center gap-2 hover:bg-accent/50 rounded px-2 py-1 -mx-2 -my-1"
        >
          {isExpanded ? (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          )}
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            My Stuff
            {bookmarkedOnly && <Pin className="h-2.5 w-2.5" />}
          </h3>
          {totalCount > 0 && (
            <Badge
              variant={overdueCount > 0 ? 'destructive' : 'secondary'}
              className="text-xs"
            >
              {totalCount}
            </Badge>
          )}
        </button>

        <div className="flex items-center gap-1">
          {cached && (
            <div
              className={cn(
                'flex items-center gap-1 text-xs',
                stale ? 'text-yellow-600' : 'text-muted-foreground'
              )}
            >
              <Clock className="h-3 w-3" />
              <span className="text-[10px]">{cachedAt ? getRelativeTime(cachedAt) : ''}</span>
              {stale && !isLoading && (
                <span className="text-[9px] font-medium">· stale</span>
              )}
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            title="Weekly triage preset (Hop internal + overdue)"
            onClick={openBasecampTriageTab}
            data-testid="my-stuff-sidebar-triage-btn"
          >
            <ListTodo className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-6 w-6',
              stale && !isLoading && 'text-yellow-600 hover:text-yellow-500',
              isLoading && 'text-blue-500'
            )}
            onClick={() => refetch(true)}
            disabled={isLoading}
            title={stale ? 'Data is stale - click to refresh' : cached ? 'Refresh data' : 'Load data'}
          >
            <RefreshCw className={cn('h-3 w-3', isLoading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="flex-1 overflow-y-auto min-h-0 border-t border-border">
          {isLoading && cached && (
            <div className="mx-2 mt-2 px-2 py-1.5 rounded-md bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-center gap-2 text-xs text-blue-600">
                <RefreshCw className="h-3 w-3 animate-spin" />
                <span>Syncing with Basecamp...</span>
              </div>
            </div>
          )}
          <MyStuffSummary
            assignments={assignments}
            isLoading={isLoading}
            error={error}
            cachedAt={cachedAt}
            onRefresh={() => refetch(true)}
            variant="widget"
          />
        </div>
      )}
    </div>
  );
}
